// Offline-Aware Form Components
import React from 'react';
import { useOfflineMode } from '../../hooks/useOfflineMode.js';
import { getOfflineDisabledClasses } from '../../utils/offlineUtils.js';
import { OfflineActionGuard } from './OfflineActionGuard.jsx';

/**
 * Offline-aware button component that automatically disables when offline
 */
export function OfflineButton({
  variant = 'primary',
  featureType = 'modify',
  children,
  disabled = false,
  className = '',
  ...props
}) {
  const { readOnlyMode } = useOfflineMode();

  const baseClasses = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    danger: 'btn-danger',
    outline: 'btn-outline',
  };

  const buttonClass = `${baseClasses[variant] || baseClasses.primary} ${className}`;
  const isDisabled =
    disabled ||
    (readOnlyMode &&
      ['create', 'edit', 'delete', 'schedule', 'import', 'assign', 'modify'].includes(featureType));

  return (
    <OfflineActionGuard featureType={featureType}>
      <button
        {...props}
        disabled={isDisabled}
        className={`${buttonClass} ${getOfflineDisabledClasses(isDisabled)}`}
      >
        {children}
      </button>
    </OfflineActionGuard>
  );
}

/**
 * Offline-aware form input that becomes read-only when offline
 */
export function OfflineInput({
  featureType = 'edit',
  readOnlyOffline = true,
  className = '',
  ...props
}) {
  const { readOnlyMode } = useOfflineMode();

  const shouldBeReadOnly = props.readOnly || (readOnlyOffline && readOnlyMode);
  const isDisabled = props.disabled || (readOnlyMode && featureType === 'edit');

  return (
    <input
      {...props}
      readOnly={shouldBeReadOnly}
      disabled={isDisabled}
      className={`form-input ${className} ${getOfflineDisabledClasses(isDisabled)}`}
    />
  );
}

/**
 * Offline-aware textarea that becomes read-only when offline
 */
export function OfflineTextarea({
  featureType = 'edit',
  readOnlyOffline = true,
  className = '',
  ...props
}) {
  const { readOnlyMode } = useOfflineMode();

  const shouldBeReadOnly = props.readOnly || (readOnlyOffline && readOnlyMode);
  const isDisabled = props.disabled || (readOnlyMode && featureType === 'edit');

  return (
    <textarea
      {...props}
      readOnly={shouldBeReadOnly}
      disabled={isDisabled}
      className={`form-input ${className} ${getOfflineDisabledClasses(isDisabled)}`}
    />
  );
}

/**
 * Offline-aware select that becomes disabled when offline
 */
export function OfflineSelect({ featureType = 'edit', className = '', children, ...props }) {
  const { readOnlyMode } = useOfflineMode();

  const isDisabled = props.disabled || (readOnlyMode && featureType === 'edit');

  return (
    <select
      {...props}
      disabled={isDisabled}
      className={`form-input ${className} ${getOfflineDisabledClasses(isDisabled)}`}
    >
      {children}
    </select>
  );
}

/**
 * Form wrapper that shows read-only mode banner and disables form when offline
 */
export function OfflineForm({
  featureType = 'edit',
  showReadOnlyBanner = true,
  className = '',
  children,
  ...props
}) {
  const { readOnlyMode } = useOfflineMode();

  return (
    <div className={className}>
      {readOnlyMode && showReadOnlyBanner && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3">
          <div className="flex items-center">
            <svg className="mr-2 h-5 w-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-sm text-amber-700">
              Form is read-only while offline. Connect to internet to make changes.
            </span>
          </div>
        </div>
      )}
      <form {...props} className={getOfflineDisabledClasses(readOnlyMode)}>
        {children}
      </form>
    </div>
  );
}
